/**
 * 后台 HTTP client：Bearer access token + refresh 自动重试 + 契约错误解析。
 * access token 存内存（不进 localStorage——08 安全基线）；refresh 靠 HttpOnly Cookie。
 */
import type { ErrorBody } from '@chronospeak/contracts';

const API_BASE = '/api/v1';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields?: Record<string, string>;

  constructor(status: number, body: ErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fields = body.fields;
  }
}

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** 401 时的会话失效回调（App 级注册：跳登录页） */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(fn: (() => void) | null): void {
  onSessionExpired = fn;
}

async function request<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  // access 过期 → 尝试静默刷新一次后重放（防循环：reused flag）
  if (res.status === 401 && !retried && !path.startsWith('/admin/auth/')) {
    const refreshRes = await fetch(`${API_BASE}/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (refreshRes.ok) {
      const body = (await refreshRes.json()) as { accessToken: string };
      setAccessToken(body.accessToken);
      return request<T>(path, init, true);
    }
    setAccessToken(null);
    onSessionExpired?.();
  }

  if (!res.ok) {
    let body: ErrorBody | null = null;
    try {
      body = (await res.json()) as ErrorBody;
    } catch {
      // 非 JSON 错误体
    }
    throw new ApiError(
      res.status,
      body?.error ?? { code: 'INTERNAL_ERROR', message: `请求失败（${res.status}）` },
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const adminHttp = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(body !== undefined
        ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        : {}),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
